import { X, Pencil, PencilLine, Trash2, Upload, ChevronDown, ChevronUp, ChevronRight, Eye, MapPin, Route, Hexagon, Download, FileText, Image as ImageIcon, Save, ClipboardList, ArrowUp, ArrowDown, Folder, FolderPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Geometry, File, FilestoreNodeBrief, SystemStats } from "@/types/api-v2";
import { objectsApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useRef, useEffect, useCallback, useMemo, type DragEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { PermissionGuard } from "@/components/PermissionGuard";
import CadastralSection from "@/components/CadastralSection";
import EehSection from "@/components/EehSection";
import {
  FilePreviewDialog,
  isFilePreviewable,
  getPreviewableImages,
} from "@/components/FilePreviewDialog";
import {
  DynamicCardRenderer,
  type DynamicCardRendererHandle,
} from "@/components/card/DynamicCardRenderer";
import { useMapUiOptional } from "@/contexts/MapUiContext";
import { readFetchErrorMessage, messageFromThrownApiError } from "@/lib/geometry-api-errors";
import { ObjectOrganizationLinksPanel } from "@/components/ObjectOrganizationLinksPanel";
import { GeometryAuditCollapsible } from "@/components/GeometryAuditCollapsible";
import { ObjectCardAuditCollapsible } from "@/components/ObjectCardAuditCollapsible";
import { objectQueryKeys } from "@/lib/query-keys";
import { fetchObjectFileAsBlob } from "@/lib/object-file-download";
import { uploadObjectFile } from "@/lib/filestore-upload";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPanelChrome } from "@/components/map-libre/MapPanelChrome";
import { MAP_PANEL_Z } from "@/lib/map-panel-z";
import { cn } from "@/lib/utils";

function filestoreNodeToFile(n: FilestoreNodeBrief, objectId: number): File {
  const t = n.created_at ?? n.updated_at ?? new Date(0).toISOString();
  return {
    id: n.id,
    object_id: n.object_id ?? objectId,
    name: n.name,
    path: n.path ?? "",
    mimetype: n.mimetype ?? "application/octet-stream",
    size: n.size ?? 0,
    path_display: n.path_display,
    parent_node_id: n.parent_id ?? undefined,
    node_id: n.id,
    created_at: t,
    updated_at: n.updated_at ?? t,
  };
}

// Компонент диалога подтверждения удаления файла
function FileDeleteDialog({ 
  file, 
  objectId, 
  onDeleted 
}: { 
  file: File; 
  objectId: number; 
  onDeleted: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/v1/objects/${objectId}/files/${file.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to delete file");
      }
      
      toast({
        title: "Файл удален",
        description: "Файл успешно удален из объекта",
      });
      
      setIsOpen(false);
      onDeleted();
    } catch (error) {
      console.error("Ошибка удаления файла:", error);
      toast({
        title: "Ошибка удаления",
        description: "Не удалось удалить файл",
        variant: "destructive",
      });
    }
  };
  
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-destructive hover:bg-destructive/15 hover:text-destructive dark:hover:bg-destructive/25"
        onClick={() => setIsOpen(true)}
        title="Удалить"
      >
        <Trash2 className="h-3 w-3" />
      </Button>
      
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Подтверждение удаления</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить файл "{file.name}"? Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function GeometryDeleteDialog({
  geometry,
  objectId,
}: {
  geometry: Geometry;
  objectId: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const mapUi = useMapUiOptional();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `/api/v1/objects/${objectId}/geometries/${geometry.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        const msg = await readFetchErrorMessage(response);
        throw new Error(msg);
      }

      queryClient.setQueryData<SystemStats>(["stats"], (old) => {
        if (!old) return old;
        return {
          ...old,
          totalGeometries: Math.max(0, (old.totalGeometries || 0) - 1),
        };
      });

      toast({ title: "Геометрия удалена" });
      queryClient.removeQueries({ queryKey: ["geometry-audit", geometry.id] });
      queryClient.invalidateQueries({ queryKey: ["objects", objectId, "geometries"] });
      queryClient.invalidateQueries({ queryKey: ["geometries"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      mapUi?.refreshMapVectorTiles();
      setIsOpen(false);
    } catch (error) {
      console.error("Ошибка удаления геометрии:", error);
      toast({
        title: "Ошибка удаления",
        description: "Не удалось удалить геометрию",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const label = geometry.name?.trim() || `Геометрия #${geometry.id}`;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-destructive hover:bg-destructive/15 hover:text-destructive dark:hover:bg-destructive/25"
        title="Удалить"
        onClick={() => setIsOpen(true)}
      >
        <Trash2 className="h-3 w-3" />
      </Button>

      <AlertDialog open={isOpen} onOpenChange={(open) => !isDeleting && setIsOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Подтверждение удаления</AlertDialogTitle>
            <AlertDialogDescription>
              Удалить геометрию «{label}»? Действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
            >
              {isDeleting ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface ObjectPanelProps {
  objectId: number;
  geometries: Geometry[];
  files: File[];
  focusedGeometryId?: number | null;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onClose: () => void;
  isLoading: boolean;
  variant?: "floating" | "embedded";
  /** Уведомление родителя о несохранённых правках runtime-карточки. */
  onCardDirtyChange?: (dirty: boolean) => void;
}

export default function ObjectPanel({
  objectId,
  geometries,
  files,
  focusedGeometryId = null,
  activeTab,
  onTabChange,
  onClose,
  isLoading,
  variant = "floating",
  onCardDirtyChange,
}: ObjectPanelProps) {
  const mapUi = useMapUiOptional();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);
  const [cardEditMode, setCardEditMode] = useState<"view" | "edit">("view");
  const cardRef = useRef<DynamicCardRendererHandle>(null);
  const [cardSavePending, setCardSavePending] = useState(false);
  const [cardDirty, setCardDirty] = useState(false);
  const [showCardDiscardDialog, setShowCardDiscardDialog] = useState(false);
  const pendingCardLeaveRef = useRef<(() => void) | null>(null);
  const geometryRowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  /** Чтобы не вызывать fitBounds при каждом refetch списка геометрий для того же фокуса MVT. */
  const flewMvtFocusRef = useRef<number | null>(null);
  const [folderTrail, setFolderTrail] = useState<Array<{ id: number | null; name: string }>>([
    { id: null, name: "Корень" },
  ]);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const lastFolderCrumb = folderTrail[folderTrail.length - 1] ?? { id: null as number | null, name: "Корень" };
  const currentParentId = lastFolderCrumb.id;

  // Fetch object data (нужен до запроса filestore, чтобы не дёргать VFS при отсутствии объекта)
  const { data: object, isLoading: objectLoading, error: objectError } = useQuery({
    queryKey: ["objects", objectId],
    enabled: !!objectId,
    queryFn: () => objectsApi.get(objectId),
  });

  const { data: filestoreNodes, isLoading: filestoreNodesLoading, error: filestoreNodesError } = useQuery({
    queryKey: objectQueryKeys.filestoreNodes(objectId, currentParentId),
    queryFn: () => objectsApi.getFilestoreNodes(objectId, currentParentId),
    enabled: Boolean(object) && activeTab === "files",
  });

  const sortedFilestoreNodes = useMemo(() => {
    if (!filestoreNodes?.length) return [];
    return [...filestoreNodes].sort((a, b) => {
      if (a.kind === "folder" && b.kind !== "folder") return -1;
      if (a.kind !== "folder" && b.kind === "folder") return 1;
      return a.name.localeCompare(b.name, "ru", { sensitivity: "base" });
    });
  }, [filestoreNodes]);

  const sortedGeometries = useMemo(() => {
    return [...geometries].sort((a, b) => {
      const da = a.display_order ?? 0;
      const db = b.display_order ?? 0;
      if (da !== db) return da - db;
      return a.id - b.id;
    });
  }, [geometries]);

  const focusMapGeometryCaption = useMemo(() => {
    if (focusedGeometryId == null) return "";
    const g = geometries.find((x) => x.id === focusedGeometryId);
    const label = g?.name ?? `#${focusedGeometryId}`;
    const sem = g?.geometry_kls_type?.trim();
    return sem ? `${label} (${sem})` : label;
  }, [geometries, focusedGeometryId]);

  const reorderGeometriesMutation = useMutation({
    mutationFn: async ({
      geometryId,
      dir,
    }: {
      geometryId: number;
      dir: "up" | "down";
    }) => {
      const sorted = [...geometries].sort((a, b) => {
        const da = a.display_order ?? 0;
        const db = b.display_order ?? 0;
        if (da !== db) return da - db;
        return a.id - b.id;
      });
      const idx = sorted.findIndex((x) => x.id === geometryId);
      if (idx < 0) return;
      const j = dir === "up" ? idx - 1 : idx + 1;
      if (j < 0 || j >= sorted.length) return;
      const next = [...sorted];
      [next[idx], next[j]] = [next[j], next[idx]];
      const patches: { id: number; order: number }[] = [];
      next.forEach((geom, i) => {
        const want = (i + 1) * 10;
        const cur = geom.display_order ?? 0;
        if (cur !== want) patches.push({ id: geom.id, order: want });
      });
      await Promise.all(
        patches.map((p) =>
          objectsApi.updateGeometry(objectId, p.id, { display_order: p.order }),
        ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["objects", objectId, "geometries"] });
      queryClient.invalidateQueries({ queryKey: ["geometries"] });
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === "geometry-audit",
      });
    },
    onError: (e) => {
      toast({
        title: "Порядок геометрий",
        description: messageFromThrownApiError(e, "Не удалось изменить порядок"),
        variant: "destructive",
      });
    },
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    setCardEditMode("view");
  }, [objectId]);

  useEffect(() => {
    setFolderTrail([{ id: null, name: "Корень" }]);
  }, [objectId]);

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!object) throw new Error("Object not loaded");
      return objectsApi.createFilestoreFolder(object.id, {
        name: name.trim(),
        parent_id: currentParentId,
      });
    },
    onSuccess: () => {
      if (!object) return;
      setNewFolderOpen(false);
      setNewFolderName("");
      queryClient.invalidateQueries({ queryKey: objectQueryKeys.filestoreNodes(objectId, currentParentId) });
      queryClient.invalidateQueries({ queryKey: objectQueryKeys.files(objectId) });
    },
    onError: (e) => {
      toast({
        title: "Папка не создана",
        description: messageFromThrownApiError(e, "Не удалось создать папку"),
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (!focusedGeometryId) {
      flewMvtFocusRef.current = null;
      return;
    }
    const geometryExists = geometries.some((g) => g.id === focusedGeometryId);
    if (!geometryExists) return;
    /* Не уводим с «Карточки»: кнопка «Атрибуты в карточке» задаёт focus + вкладку description; иначе этот эффект сразу откатывал на «Геометрию». */
    if (activeTab !== "geometry" && activeTab !== "description") {
      onTabChange("geometry");
    }
    const selected = geometries.find((g) => g.id === focusedGeometryId);
    if (selected && mapUi) {
      mapUi.setSelectedGeometry(selected);
      if (flewMvtFocusRef.current !== focusedGeometryId) {
        mapUi.focusGeometryOnMap(selected);
        flewMvtFocusRef.current = focusedGeometryId;
      }
    }
    const target = geometryRowRefs.current.get(focusedGeometryId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeTab, focusedGeometryId, geometries, mapUi, onTabChange]);

  const handleDeleteObject = async () => {
    if (!object) return;
    try {
      await objectsApi.delete(object.id);
      
      setIsDeleteDialogOpen(false);
      
      toast({
        title: "Объект удален",
        description: "Объект успешно удален из системы",
      });
      
      queryClient.invalidateQueries({ queryKey: ["objects"] });
      queryClient.invalidateQueries({ queryKey: ["objects", object.id, "geometries"] });
      mapUi?.refreshMapVectorTiles();

      onClose();
    } catch (error) {
      console.error("Error deleting object:", error);
      toast({
        title: "Ошибка удаления",
        description: "Не удалось удалить объект",
        variant: "destructive",
      });
    }
  };

  const uploadFile = useCallback(
    async (f: globalThis.File) => {
      if (!object) return;
      const parentId = folderTrail[folderTrail.length - 1]?.id ?? null;

      setIsUploading(true);
      setUploadProgress(0);

      try {
        await uploadObjectFile({
          objectId: object.id,
          file: f,
          parentNodeId: parentId,
          onProgress: setUploadProgress,
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast({ title: "Файл загружен", description: f.name });
        queryClient.invalidateQueries({ queryKey: objectQueryKeys.files(object.id) });
        queryClient.invalidateQueries({ queryKey: ["objects", object.id, "filestore"] });
      } catch (error) {
        console.error("Ошибка загрузки файла:", error);
        toast({
          title: "Ошибка загрузки",
          description: messageFromThrownApiError(error, "Не удалось загрузить файл"),
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [object, toast, queryClient, folderTrail],
  );

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) uploadFile(droppedFile);
  }, [uploadFile]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setIsDragActive(true); }, []);
  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setIsDragActive(false); }, []);

  const getGeometryIcon = (type: string) => {
    switch (type) {
      case "Point": return MapPin;
      case "LineString": return Route;
      default: return Hexagon;
    }
  };

  const getFileIcon = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext || "")) return ImageIcon;
    return FileText;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  useEffect(() => {
    setCardEditMode("view");
    setCardDirty(false);
  }, [objectId]);

  useEffect(() => {
    onCardDirtyChange?.(cardDirty);
  }, [cardDirty, onCardDirtyChange]);

  useEffect(() => {
    if (!cardDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [cardDirty]);

  const requestCardLeave = useCallback(
    (action: () => void) => {
      if (!cardDirty) {
        action();
        return;
      }
      pendingCardLeaveRef.current = action;
      setShowCardDiscardDialog(true);
    },
    [cardDirty],
  );

  const handleCancelCardEdit = useCallback(() => {
    requestCardLeave(() => cardRef.current?.cancel());
  }, [requestCardLeave]);

  const handleTabChange = useCallback(
    (tab: string) => {
      if (tab === activeTab) return;
      if (cardDirty && activeTab === "description" && tab !== "description") {
        requestCardLeave(() => onTabChange(tab));
        return;
      }
      onTabChange(tab);
    },
    [activeTab, cardDirty, onTabChange, requestCardLeave],
  );

  const handleClosePanel = useCallback(() => {
    requestCardLeave(onClose);
  }, [onClose, requestCardLeave]);

  const isEmbedded = variant === "embedded";
  const floatingWrapperClass = cn(
    "absolute right-4 top-4 bottom-4 flex w-[500px] flex-col overflow-hidden",
    MAP_PANEL_Z.mapPanel,
  );
  const embeddedWrapperClass = "flex h-full min-h-0 flex-col overflow-hidden";

  if (objectLoading) {
    const skeleton = (
      <div className="p-4">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-4 w-32 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>
    );
    if (isEmbedded) return skeleton;
    return (
      <div className={floatingWrapperClass}>
        <MapPanelChrome bare className="h-full">
          {skeleton}
        </MapPanelChrome>
      </div>
    );
  }

  if (objectError || !object) {
    const errText =
      objectError instanceof Error
        ? objectError.message
        : objectError
          ? String(objectError)
          : "Объект не найден или ответ API пустой";
    const errorContent = (
      <div className="p-4">
        <div className="text-red-500">Ошибка загрузки объекта</div>
        <p className="mt-2 break-words text-xs text-muted-foreground">{errText}</p>
        <Button onClick={handleClosePanel} className="mt-2">
          Закрыть
        </Button>
      </div>
    );
    if (isEmbedded) return errorContent;
    return (
      <div className={floatingWrapperClass}>
        <MapPanelChrome bare className="h-full">
          {errorContent}
        </MapPanelChrome>
      </div>
    );
  }

  const headerActions = (
    <>
      {cardDirty && (
        <span
          className="mr-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-200"
          title="Есть несохранённые изменения карточки"
        >
          несохранено
        </span>
      )}
      {cardEditMode === "view" && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex h-7 items-center px-2"
          title="Редактировать карточку (объект и атрибуты геометрий по шаблону)"
          onClick={() => setCardEditMode("edit")}
        >
          <PencilLine className="mr-1 h-3.5 w-3.5" />
          <span className="text-xs">Изменить</span>
        </Button>
      )}
      {cardEditMode === "edit" && (
        <>
          {activeTab === "description" && (
            <Button
              type="button"
              size="sm"
              className="flex h-7 items-center px-2"
              title="Сохранить карточку"
              onClick={() => void cardRef.current?.save()}
              disabled={cardSavePending}
            >
              <Save className="mr-1 h-3.5 w-3.5" />
              <span className="text-xs">{cardSavePending ? "Сохранение..." : "Сохранить"}</span>
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="flex h-7 items-center px-2"
            title="Отменить изменения и выйти из режима редактирования"
            onClick={handleCancelCardEdit}
            disabled={cardSavePending}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            <span className="text-xs">Отмена</span>
          </Button>
        </>
      )}
      <PermissionGuard permission="delete_objects">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex h-7 items-center border-red-200 px-2 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          title="Удалить"
          onClick={() => setIsDeleteDialogOpen(true)}
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" />
          <span className="text-xs">Удалить</span>
        </Button>
      </PermissionGuard>
      {!isEmbedded && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            title={isCollapsed ? "Развернуть" : "Свернуть"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsCollapsed((prev) => !prev);
            }}
          >
            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={handleClosePanel}
          >
            <X className="h-4 w-4" />
          </Button>
        </>
      )}
    </>
  );

  const panelBody = !isCollapsed ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <TabsList className="grid w-full flex-shrink-0 grid-cols-6">
          <TabsTrigger value="description" title="Объект и атрибуты геометрий по шаблону карточки">
            Карточка
          </TabsTrigger>
          <TabsTrigger value="geometry" title="Список геометрий, показ на карте, рисование и редактирование контура">
            Геометрия
          </TabsTrigger>
          <TabsTrigger value="links">Связи</TabsTrigger>
          <TabsTrigger value="eeh" title="Экономически-эксплуатационные характеристики: контракты и мероприятия">
            ЭЭХ
          </TabsTrigger>
          <TabsTrigger value="cadastral">Кадастры</TabsTrigger>
          <TabsTrigger value="files">Файлы</TabsTrigger>
        </TabsList>
        
        <TabsContent value="description" className="p-3 flex-1 min-h-0 overflow-y-auto data-[state=inactive]:hidden">
          <DynamicCardRenderer
            ref={cardRef}
            objectId={objectId}
            focusedGeometryId={focusedGeometryId}
            mode={cardEditMode}
            onModeChange={setCardEditMode}
            showBottomActionBar={false}
            onToolbarStateChange={(s) => {
              setCardSavePending(s.isSaving);
              setCardDirty(s.isDirty);
            }}
          />
          <ObjectCardAuditCollapsible objectId={objectId} />
        </TabsContent>

        <TabsContent
          value="links"
          className="p-3 flex-1 min-h-0 overflow-y-auto data-[state=inactive]:hidden"
        >
          <ObjectOrganizationLinksPanel objectId={objectId} />
        </TabsContent>
        
        <TabsContent value="geometry" className="p-3 flex flex-col flex-1 min-h-0 overflow-hidden data-[state=inactive]:hidden data-[state=active]:flex">
          {focusedGeometryId != null && mapUi?.clearGeometryFocus && (
            <div className="mb-2 flex flex-shrink-0 items-center justify-between gap-2 rounded-md border border-primary/25 bg-primary/10 px-2.5 py-1.5 text-xs text-foreground dark:border-primary/35 dark:bg-primary/15">
              <span className="min-w-0 leading-snug">
                <span className="text-muted-foreground">Выделение с карты: </span>
                геометрия «{focusMapGeometryCaption}»
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-7 shrink-0 border border-border bg-background text-foreground hover:bg-muted dark:bg-card"
                onClick={() => mapUi.clearGeometryFocus()}
              >
                Снять выделение
              </Button>
            </div>
          )}
          {isLoading ? (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-md border border-border p-2">
                  <Skeleton className="h-4 w-3/4 mb-1" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
                {geometries.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Геометрии отсутствуют
                  </div>
                ) : (
                  sortedGeometries.map((geometry, rowIndex) => {
                    const typeStr = typeof geometry.type === 'object' ? JSON.stringify(geometry.type) : String(geometry.type);
                    const TypeIcon = getGeometryIcon(typeStr);
                    return (
                      <div
                        key={geometry.id}
                        ref={(el) => {
                          if (el) geometryRowRefs.current.set(geometry.id, el);
                          else geometryRowRefs.current.delete(geometry.id);
                        }}
                        className={`rounded-md border border-border p-2.5 transition-colors ${
                          focusedGeometryId === geometry.id
                            ? "border-primary/55 bg-primary/12 text-foreground ring-1 ring-primary/25 dark:bg-primary/20 dark:ring-primary/30"
                            : "text-foreground hover:bg-muted/60"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-foreground">
                                {geometry.name}
                                {geometry.geometry_kls_type?.trim() ? (
                                  <span className="text-muted-foreground font-normal">
                                    {" "}
                                    ({geometry.geometry_kls_type.trim()})
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-xs text-muted-foreground">{typeStr}</div>
                            </div>
                          </div>
                          <div className="flex shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              title="Выше в списке (display_order)"
                              disabled={
                                reorderGeometriesMutation.isPending || rowIndex === 0
                              }
                              onClick={() =>
                                reorderGeometriesMutation.mutate({
                                  geometryId: geometry.id,
                                  dir: "up",
                                })
                              }
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              title="Ниже в списке (display_order)"
                              disabled={
                                reorderGeometriesMutation.isPending ||
                                rowIndex >= sortedGeometries.length - 1
                              }
                              onClick={() =>
                                reorderGeometriesMutation.mutate({
                                  geometryId: geometry.id,
                                  dir: "down",
                                })
                              }
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              title="Показать на карте (приблизить). Также выбирает геометрию для вкладки «Ассистент»"
                              onClick={() => {
                                if (mapUi) {
                                  mapUi.focusGeometryOnMap(geometry);
                                  mapUi.setSelectedGeometry(geometry);
                                } else {
                                  window.dispatchEvent(
                                    new CustomEvent("focusGeometryOnMap", {
                                      detail: { geometry, source: "object" },
                                    })
                                  );
                                }
                              }}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              title="Атрибуты в карточке (шаблон, KLS)"
                              onClick={() => {
                                mapUi?.setFocusedGeometryId(geometry.id);
                                onTabChange("description");
                              }}
                            >
                              <ClipboardList className="h-3 w-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              title="Редактировать контур на карте"
                              onClick={() => {
                                if (mapUi) {
                                  mapUi.requestEditGeometry(geometry, object.id);
                                } else {
                                  window.dispatchEvent(new CustomEvent('editGeometry', { 
                                    detail: { geometry, objectId: object.id } 
                                  }));
                                }
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <PermissionGuard permission="delete_geometries">
                              <GeometryDeleteDialog geometry={geometry} objectId={object.id} />
                            </PermissionGuard>
                          </div>
                        </div>
                        <GeometryAuditCollapsible geometryId={geometry.id} />
                      </div>
                    );
                  })
                )}
              </div>
              <Button 
                size="sm"
                className="w-full flex-shrink-0 mt-3 h-8"
                onClick={() => {
                  if (mapUi) {
                    mapUi.requestStartDrawing('draw-polygon', object.id);
                  } else {
                    window.dispatchEvent(new CustomEvent('activateDrawingTool', { 
                      detail: { tool: 'draw-polygon', objectId: object.id } 
                    }));
                  }
                }}
              >
                Добавить геометрию
              </Button>
            </>
          )}
        </TabsContent>
        
        <TabsContent value="eeh" className="p-3 flex-1 min-h-0 overflow-y-auto data-[state=inactive]:hidden">
          <EehSection
            objectId={object.id}
            geometries={geometries}
            onOpenCardTab={() => onTabChange("description")}
          />
        </TabsContent>

        <TabsContent value="cadastral" className="p-4 flex-1 min-h-0 overflow-y-auto data-[state=inactive]:hidden">
          <CadastralSection objectId={object.id} />
        </TabsContent>
        
        <TabsContent value="files" className="p-3 flex flex-col flex-1 min-h-0 overflow-hidden data-[state=inactive]:hidden data-[state=active]:flex">
          {object && (
            <>
              <div className="mb-2 flex min-h-7 flex-wrap items-center gap-0.5 text-xs text-muted-foreground">
                {folderTrail.map((crumb, i) => (
                  <div key={`${i}-${String(crumb.id)}`} className="flex min-w-0 max-w-full items-center">
                    {i > 0 && <ChevronRight className="mx-0.5 h-3.5 w-3.5 shrink-0" />}
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto min-w-0 max-w-[11rem] truncate p-0 px-0.5 text-xs font-normal text-foreground"
                      onClick={() => setFolderTrail((p) => p.slice(0, i + 1))}
                    >
                      {crumb.name}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="mb-2 flex flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    setNewFolderName("");
                    setNewFolderOpen(true);
                  }}
                >
                  <FolderPlus className="mr-1.5 h-3.5 w-3.5" />
                  Новая папка
                </Button>
              </div>
            </>
          )}

          <div className="flex-1 min-h-0 space-y-2 overflow-y-auto pr-1">
            {filestoreNodesError && object && (
              <div className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
                Не удалось загрузить список файлов
              </div>
            )}
            {filestoreNodesLoading && activeTab === "files" && (
              <div className="space-y-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            )}

            {!filestoreNodesLoading && object && sortedFilestoreNodes.length === 0 && !isUploading && !filestoreNodesError && (
              <div className="py-4 text-center text-sm text-muted-foreground">В этой папке пока пусто</div>
            )}

            {object &&
              !filestoreNodesLoading &&
              sortedFilestoreNodes.map((node) => {
                if (node.kind === "folder") {
                  return (
                    <button
                      key={node.id}
                      type="button"
                      onClick={() =>
                        setFolderTrail((prev) => [...prev, { id: node.id, name: node.name }])
                      }
                      className="flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border p-2.5 text-left transition-colors hover:bg-muted/60"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <Folder className="h-4 w-4 shrink-0 text-amber-600/90 dark:text-amber-500/90" />
                        <span className="truncate text-sm font-medium text-foreground">{node.name}</span>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </button>
                  );
                }
                const file = filestoreNodeToFile(node, object.id);
                const FIcon = getFileIcon(file.name);
                return (
                  <div
                    key={file.id}
                    className="rounded-md border border-border p-2.5 transition-colors hover:bg-muted/60"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <FIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-foreground">{file.name}</div>
                          <div className="text-xs text-muted-foreground">{formatFileSize(file.size)}</div>
                        </div>
                      </div>
                      <div className="flex shrink-0">
                        {isFilePreviewable(file) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title="Просмотр"
                            onClick={() => {
                              const imageFiles = getPreviewableImages(files);
                              const isImage = imageFiles.some((f) => f.id === file.id);
                              if (isImage && imageFiles.length > 1) {
                                setPreviewFiles(imageFiles);
                                setPreviewInitialIndex(
                                  imageFiles.findIndex((f) => f.id === file.id),
                                );
                              } else {
                                setPreviewFiles([file]);
                                setPreviewInitialIndex(0);
                              }
                              setPreviewOpen(true);
                            }}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Скачать"
                          onClick={async () => {
                            try {
                              const blob = await fetchObjectFileAsBlob(object.id, file.id);
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = file.name;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error("Ошибка скачивания:", error);
                              toast({
                                title: "Ошибка",
                                description: "Не удалось скачать файл",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        <PermissionGuard permission="delete_files">
                          <FileDeleteDialog
                            file={file}
                            objectId={object.id}
                            onDeleted={() => {
                              queryClient.invalidateQueries({ queryKey: objectQueryKeys.files(object.id) });
                              queryClient.invalidateQueries({
                                queryKey: ["objects", object.id, "filestore"],
                              });
                            }}
                          />
                        </PermissionGuard>
                      </div>
                    </div>
                  </div>
                );
              })}

            {isUploading && (
              <div className="rounded-md border border-border p-2.5">
                <div className="mb-1 text-xs text-muted-foreground">Загрузка... {uploadProgress}%</div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-1.5 rounded-full bg-primary transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {object && (
            <div
              className={`mt-3 flex-shrink-0 cursor-pointer rounded-md border-2 border-dashed p-3 text-center transition-colors ${
                isDragActive
                  ? "border-primary/60 bg-primary/10 dark:bg-primary/15"
                  : "border-border hover:border-muted-foreground/40"
              }`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
              <div className="text-xs text-muted-foreground">
                {isDragActive ? "Отпустите файл" : "Перетащите сюда или нажмите, чтобы выбрать"}
              </div>
              {currentParentId != null && (
                <div className="mt-0.5 text-[11px] text-muted-foreground/80">Загрузка в текущую папку</div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadFile(f);
                }}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
        </div>
  ) : null;

  const chrome = (
    <MapPanelChrome
      bare={isEmbedded}
      className={isEmbedded ? "h-full" : undefined}
      title={isEmbedded ? undefined : object.name}
      headerActions={headerActions}
      bodyClassName="flex min-h-0 flex-col overflow-hidden p-0"
    >
      {panelBody}
    </MapPanelChrome>
  );

  return (
    <>
      {isEmbedded ? (
        <div className={embeddedWrapperClass}>{chrome}</div>
      ) : (
        <div className={cn(floatingWrapperClass, !isCollapsed && "bottom-4")}>{chrome}</div>
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие удалит объект "{object.name}" и все связанные с ним геометрии и файлы. Это действие нельзя
              отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteObject} className="bg-red-500 hover:bg-red-600">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showCardDiscardDialog}
        onOpenChange={(open) => {
          setShowCardDiscardDialog(open);
          if (!open) pendingCardLeaveRef.current = null;
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Несохранённые изменения карточки</AlertDialogTitle>
            <AlertDialogDescription>
              Есть несохранённые правки. Продолжить и потерять изменения?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Вернуться</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const next = pendingCardLeaveRef.current;
                pendingCardLeaveRef.current = null;
                setShowCardDiscardDialog(false);
                cardRef.current?.cancel();
                setCardDirty(false);
                next?.();
              }}
            >
              Продолжить без сохранения
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FilePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        objectId={object.id}
        files={previewFiles}
        initialIndex={previewInitialIndex}
      />

      <Dialog
        open={newFolderOpen}
        onOpenChange={(open) => {
          setNewFolderOpen(open);
          if (!open) setNewFolderName("");
        }}
      >
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Новая папка</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="filestore-new-folder-name">Название</Label>
            <Input
              id="filestore-new-folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Например, Документы"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newFolderName.trim() && !createFolderMutation.isPending) {
                  e.preventDefault();
                  createFolderMutation.mutate(newFolderName.trim());
                }
              }}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setNewFolderOpen(false);
                setNewFolderName("");
              }}
            >
              Отмена
            </Button>
            <Button
              type="button"
              disabled={!newFolderName.trim() || createFolderMutation.isPending}
              onClick={() => createFolderMutation.mutate(newFolderName.trim())}
            >
              {createFolderMutation.isPending ? "Создание…" : "Создать"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
